import React, { useEffect, useState, useCallback } from "react";
import apiService from "../services/apiService";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import {
  Box,
  Menu,
  CssBaseline,
  Switch,
  Tooltip,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";

function CriticalJobs() {
  // Get current local date in YYYY-MM-DD format
  const getCurrentLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = getCurrentLocalDate();

  /* ================= STATES ================= */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  // Column filter states
  const [columnFilters, setColumnFilters] = useState({});
  const [tempColumnFilters, setTempColumnFilters] = useState({});
  const [filterAnchorEls, setFilterAnchorEls] = useState({});

  const formatDate = (value) => {
    if (!value) return "";
    // Extract date part only (YYYY-MM-DD) to avoid timezone issues
    const datePart = value.split("T")[0];
    const [year, month, day] = datePart.split("-");
    const d = new Date(year, month - 1, day); // Create date in local timezone
    return d.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const validateForm = () => {
    const errors = {};

    if (!form.env_id) errors.env_id = "Environment is required";
    if (!form.app_id) errors.app_id = "Application is required";
    if (!form.job_id) errors.job_id = "Job is required";
    if (!form.date) errors.date = "Date is required";

    if (form.failure_count === "" || form.failure_count === null)
      errors.failure_count = "Failure count is required";
    else if (Number(form.failure_count) < 0)
      errors.failure_count = "Failure count cannot be negative";

    if (form.success_count === "" || form.success_count === null)
      errors.success_count = "Success count is required";
    else if (Number(form.success_count) < 0)
      errors.success_count = "Success count cannot be negative";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleToggleFlag = async (id, currentFlag) => {
    try {
      await apiService.put(`/api/critical-jobs/${id}/toggle-active`, {
        active_flag: !currentFlag, 
      });
      loadTable(); // refresh table
      
      // Show success message
      const message = currentFlag 
        ? "Critical Job Deactivated" 
        : "Critical Job Activated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Toggle failed", err);
    }
  };

  // Column filter handlers
  const handleFilterIconClick = (event, columnKey) => {
    setFilterAnchorEls((prev) => ({
      ...prev,
      [columnKey]: event.currentTarget,
    }));
    setTempColumnFilters((prev) => ({
      ...prev,
      [columnKey]: columnFilters[columnKey] || "",
    }));
  };

  const handleFilterClose = (columnKey) => {
    setFilterAnchorEls((prev) => ({
      ...prev,
      [columnKey]: null,
    }));
  };

  const applyColumnFilter = (columnKey) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: tempColumnFilters[columnKey],
    }));
    setPage(1);
    handleFilterClose(columnKey);
  };

  const clearColumnFilter = (columnKey) => {
    setColumnFilters((prev) => {
      const updated = { ...prev };
      delete updated[columnKey];
      return updated;
    });
    setTempColumnFilters((prev) => ({
      ...prev,
      [columnKey]: "",
    }));
    setPage(1);
    handleFilterClose(columnKey);
  };

  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    env_id: "",
    app_id: "",
  });
  const [visibleColumns, setVisibleColumns] = useState({
    client: true,
    env: true,
    app: true,
    date: true,
    job: true,
    failure: true,
    success: true,
  });

  const columnLabels = {
    client: "Client",
    env: "Environment",
    app: "Application",
    date: "Date",
    job: "Job",
    failure: "Failure Count",
    success: "Success Count",
  };

  const columns = Object.keys(columnLabels);

  const handleClearFilters = () => {
    setFilters({
      date_from: today,
      date_to: today,
      env_id: "",
      app_id: "",
    });
    setPage(0);
  };

  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [originalForm, setOriginalForm] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  // const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    critical_jobs_id: null,
    env_id: "",
    app_id: "",
    job_id: "",
    date: today,
    failure_count: 0,
    success_count: 0,
  });

  /* ================= API CALLS ================= */
  const loadEnvironments = async () => {
    const res = await apiService.get("/api/critical-jobs/environments");
    setEnvironments(res);
  };

  const loadApplications = async (envId) => {
    const endpoint = envId 
      ? `/api/critical-jobs/applications?env_id=${envId}`
      : `/api/critical-jobs/applications`;
    const res = await apiService.get(endpoint);
    setApplications(res);
  };

  const loadJobs = async (envId, appId) => {
    if (!envId || !appId) return setJobs([]);
    const res = await apiService.get(
      `/api/critical-jobs/jobs?env_id=${envId}&app_id=${appId}`,
    );
    setJobs(res);
  };

  const loadTable = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page + 1,
      limit: rowsPerPage,
      ...filters,
    });

    try {
      const res = await apiService.get(
        `/api/critical-jobs/paginate/list?${params}`,
      );
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error("Error loading critical jobs:", err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  /* ================= EFFECTS ================= */
  useEffect(() => {
    loadEnvironments();
    loadApplications();
  }, []);

  useEffect(() => {
    loadTable();
  }, [loadTable]);

  // Track form changes for edit mode
  useEffect(() => {
    if (editMode && Object.keys(originalForm).length > 0) {
      const hasChanged = JSON.stringify(form) !== JSON.stringify(originalForm);
      setIsFormChanged(hasChanged);
    }
  }, [form, originalForm, editMode]);

  /* ================= HANDLERS ================= */
  const resetForm = () => {
    setForm({
      critical_jobs_id: null,
      env_id: "",
      app_id: "",
      job_id: "",
      date: today,
      failure_count: 0,
      success_count: 0,
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (editMode) {
        await apiService.put(
          `/api/critical-jobs/${form.critical_jobs_id}`,
          form,
        );
      } else {
        await apiService.post("/api/critical-jobs", form);
      }

      setSuccessMessage("Saved successfully");
      setOpenDialog(false);
      resetForm();
      loadTable();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setFormErrors({ general: "Save failed. Try again." });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.delete(`/api/critical-jobs/${deleteId}`);
      setOpenDeleteDialog(false);
      setDeleteId(null);
      setSuccessMessage("Record deleted successfully!");
      loadTable();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };
  /* ================= RENDER ================= */
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <Box sx={{ position: "relative", minWidth: 140 }}>
            <TextField
              label="Date From"
              type="date"
              size="small"
              value={filters.date_from}
              onChange={(e) =>
                setFilters((p) => ({ ...p, date_from: e.target.value }))
              }
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: "0.75rem",
                  top: "1px",
                },
              }}
              sx={{
                minWidth: 140,
                "& .MuiInputBase-root": { height: 37 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                  color: "transparent", 
                },
                "& input::-webkit-calendar-picker-indicator": {
                  opacity: 1,
                },
              }}
            />

            {/* Formatted date INSIDE input */}
            {filters.date_from && (
              <Typography
                sx={{
                  mt: -0.5,
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-10%)",
                  fontSize: "0.75rem",
                  color: "#000",
                  pointerEvents: "none",
                }}
              >
                {formatDate(filters.date_from)}
              </Typography>
            )}
          </Box>
          <Box sx={{ position: "relative", minWidth: 140 }}>
            <TextField
              label="Date To"
              type="date"
              size="small"
              value={filters.date_to}
              onChange={(e) =>
                setFilters((p) => ({ ...p, date_to: e.target.value }))
              }
              // InputLabelProps={{ shrink: true }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: "0.75rem",
                  top: "1px",
                },
              }}
              sx={{
                minWidth: 140,
                "& .MuiInputBase-root": { height: 37 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                  color: "transparent",
                },
              }}
            />

            {filters.date_to && (
              <Typography
                sx={{
                  mt: -0.5,
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-10%)",
                  fontSize: "0.75rem",
                  color: "#000",
                  pointerEvents: "none",
                }}
              >
                {formatDate(filters.date_to)}
              </Typography>
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Environment</InputLabel>
            <Select
              value={filters.env_id}
              label="Environment"
              required
              onChange={(e) => {
                setFilters((p) => ({
                  ...p,
                  env_id: e.target.value,
                  app_id: "",
                }));
                loadApplications(e.target.value);
              }}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.75rem",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "0.75rem",
                      minHeight: 32,
                    },
                  },
                },
              }}
            >
              {environments.map((env) => (
                <MenuItem
                  key={env.env_id}
                  value={env.env_id}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {env.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Application</InputLabel>
            <Select
              value={filters.app_id}
              label="Application"
              required
              onChange={(e) =>
                setFilters((p) => ({ ...p, app_id: e.target.value }))
              }
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.75rem",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "0.75rem",
                      minHeight: 32,
                    },
                  },
                },
              }}
            >
              {/* {setFilters.map((app) => ( */}
              {applications.map((app) => (
                <MenuItem
                  key={app.app_id}
                  value={app.app_id}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* Right side: Buttons + Pagination all on the same line */}
      <Box
        display="flex"
        alignItems="right"
        justifyContent="flex-end" 
        sx={{ mb: 0, mt: -6.5, px: 4 }}
      >
        <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
          {/* Add + */}
          <Button
            onClick={() => {
              resetForm();
              setOpenDialog(true);
            }}
            sx={{
              color: "#666",
              fontWeight: "bold",
              borderRadius: "8px",
              height: "35px",
              minWidth: "50px",
              px: 1.5,
              textTransform: "none",
              fontSize: "12px",
              "&:hover": { color: "#1976d2" },
            }}
          >
            Add +
          </Button>

          {/* Clear Filters */}
          <Tooltip title="Clear Filters">
            <IconButton
              onClick={handleClearFilters}
              sx={{
                color: "#666",
                height: "35px",
                width: "30px",
                borderRadius: "8px",
                "&:hover": { color: "#1976d2" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Settings */}
          <Tooltip title="Column Customization" arrow>
            <IconButton
              onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
              sx={{
                color: "#666",
                height: "35px",
                width: "30px",
                borderRadius: "8px",
                "&:hover": { color: "#1976d2" },
              }}
            >
              <SettingsIcon style={{ width: 17, height: 17 }} />
            </IconButton>
          </Tooltip>

          {/* Left Arrow */}

          <IconButton
            onClick={() => {
              if (page > 0) setPage(page - 1);
            }}
            disabled={page === 0}
            size="small"
            sx={{
              width: "30px",
              height: "35px",
              borderRadius: "6px",
              color: "#666",
              "&:hover": { color: "#1976d2" },
              "&:disabled": { color: "#ccc" },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </IconButton>

          <Box
            sx={{
              fontSize: "13px",
              fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            {`${page * rowsPerPage + 1}-${Math.min(
              (page + 1) * rowsPerPage,
              total,
            )} of ${total}`}
          </Box>

          <IconButton
            onClick={() => {
              const totalPages = Math.ceil(total / rowsPerPage);
              if (page < totalPages - 1) setPage(page + 1);
            }}
            disabled={page >= Math.ceil(total / rowsPerPage) - 1}
            size="small"
            sx={{
              width: "30px",
              height: "35px",
              borderRadius: "6px",
              color: "#666",
              "&:hover": { color: "#1976d2" },
              "&:disabled": { color: "#ccc" },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </IconButton>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {formErrors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formErrors.general}
        </Alert>
      )}
      {/* TABLE */}

      <Box
        sx={{
          mt: 2,
          height: "calc(90vh - 160px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 230px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {columnLabels[col]}
                              <IconButton
                                size="small"
                                onClick={(e) => handleFilterIconClick(e, col)}
                                sx={{
                                  padding: "2px",
                                  "&:hover": { color: "#1976d2" },
                                }}
                              >
                                <FilterListIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent sx={{ width: 250, padding: 2 }}>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempColumnFilters[col] || ""}
                                  onChange={(e) =>
                                    setTempColumnFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearColumnFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyColumnFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>

                {/* <TableBody> */}
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={7} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : rows.length > 0 ? (
                    rows.map((r) => (
                      <TableRow key={r.critical_jobs_id}>
                        {/* ACTIONS */}
                        <TableCell>
                          <Box display="flex" gap={1} alignItems="center">
                            <IconButton
                              size="small"
                              title="Toggle Active Flag"
                              onClick={() =>
                                handleToggleFlag(
                                  r.critical_jobs_id,
                                  r.active_flag,
                                )
                              }
                            >
                              <FlagIcon
                                style={{
                                  color: r.active_flag ? "#2E9F50" : "#FF0C0C",
                                  width: 17,
                                  height: 17,
                                  transition: "color 0.2s",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Edit"
                              onClick={() => {
                                setEditMode(true);
                                const formData = {
                                  critical_jobs_id: r.critical_jobs_id,
                                  env_id: r.env_id,
                                  app_id: r.app_id,
                                  job_id: r.job_id,
                                  date: r.date ? r.date.split("T")[0] : "",
                                  failure_count: r.failure_count,
                                  success_count: r.success_count,
                                };
                                setForm(formData);
                                setOriginalForm({ ...formData }); // Store original data for comparison
                                setIsFormChanged(false); // Reset change flag
                                loadApplications(r.env_id);
                                loadJobs(r.env_id, r.app_id);
                                setOpenDialog(true);
                              }}
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: 16 }} />
                            </IconButton>

                            <IconButton
                              size="small"
                              title="Delete"
                              onClick={() =>
                                handleDeleteClick(r.critical_jobs_id)
                              }
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {visibleColumns.client && (
                          <TableCell>{r.client_name}</TableCell>
                        )}
                        {visibleColumns.env && (
                          <TableCell>{r.env_name}</TableCell>
                        )}
                        {visibleColumns.app && (
                          <TableCell>{r.app_name}</TableCell>
                        )}
                        {visibleColumns.date && (
                          <TableCell>{formatDate(r.date)}</TableCell>
                        )}
                        {visibleColumns.job && (
                          <TableCell>{r.job_name || r.job_id}</TableCell>
                        )}
                        {visibleColumns.failure && (
                          <TableCell>{r.failure_count}</TableCell>
                        )}
                        {visibleColumns.success && (
                          <TableCell>{r.success_count}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={7} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>
      </Box>
      {/* Column Visibility Menu */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={() => setSettingsAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 260,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                px: 2,
                py: 1,
                border: "1px solid #ccc",
                borderRadius: 1.5,
                bgcolor: "#fff",
              }}
            >
              <Typography fontSize="0.8rem" fontWeight="bold">
                {columnLabels[col]}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((p) => ({ ...p, [col]: e.target.checked }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>
      {/* ADD / EDIT DIALOG */}
      <ThemeProvider theme={theme}>
        <Dialog 
          open={openDialog} 
          maxWidth="md" 
          fullWidth
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDialog(false);
          }}
        >
          <DialogTitle>
            {editMode ? "Edit Critical Job" : "Add Critical Job"}
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box 
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mt: 1,
                alignItems: "center",
                "& .MuiInputBase-root": {
                  height: 40,
                  minHeight: 40,
                  boxSizing: "border-box",
                },
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  paddingTop: 0,
                  paddingBottom: 0,
                  "& .MuiSelect-select": {
                    padding: "8.5px 14px",
                    height: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "8.5px 14px",
                  fontSize: "0.875rem",
                },
                "& .MuiFormHelperText-root": {
                  marginTop: 2,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                  transform: "translate(14px, 9px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -6px) scale(0.75)",
                  },
                },
              }}
            >
           <FormControl
              fullWidth
              size="small"
              required
              error={!!formErrors.env_id}
            >
              <InputLabel>Environment</InputLabel>
              <Select
                value={form.env_id}
                label="Environment"
                onChange={(e) => {
                  setForm((p) => ({
                    ...p,
                    env_id: e.target.value,
                    app_id: "",
                    job_id: "",
                  }));
                  setFormErrors((prev) => ({ ...prev, env_id: "" }));
                  loadApplications(e.target.value);
                }}
              >
                {environments.map((env) => (
                  <MenuItem key={env.env_id} value={env.env_id} sx={{ fontSize: "0.875rem" }}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.env_id && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ fontSize: "0.7rem" }}
                >
                  {formErrors.env_id}
                </Typography>
              )}
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              required
              error={!!formErrors.app_id}
              disabled={!form.env_id}
            >
              <InputLabel>Application</InputLabel>
              <Select
                value={form.app_id}
                label="Application"
                onChange={(e) => {
                  setForm((p) => ({
                    ...p,
                    app_id: e.target.value,
                    job_id: "",
                  }));
                  setFormErrors((prev) => ({ ...prev, app_id: "" }));
                  loadJobs(form.env_id, e.target.value);
                }}
              >
                {applications.map((app) => (
                  <MenuItem key={app.app_id} value={app.app_id} sx={{ fontSize: "0.875rem" }}>
                    {app.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.app_id && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ fontSize: "0.7rem" }}
                >
                  {formErrors.app_id}
                </Typography>
              )}
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              required
              error={!!formErrors.job_id}
              disabled={!form.app_id}
            >
              <InputLabel>Job</InputLabel>
              <Select
                value={form.job_id}
                label="Job"
                displayEmpty
                onChange={(e) => {
                  setForm((p) => ({ ...p, job_id: e.target.value }));
                  setFormErrors((prev) => ({ ...prev, job_id: "" }));
                }}
              >
                <MenuItem value="">
                  
                </MenuItem>
                {jobs.map((j) => (
                  <MenuItem key={j.job_id} value={j.job_id} sx={{ fontSize: "0.875rem" }}>
                    {j.job_id} - {j.jobs_script}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.job_id && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ fontSize: "0.7rem" }}
                >
                  {formErrors.job_id}
                </Typography>
              )}
            </FormControl>

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              error={!!formErrors.date}
              helperText={formErrors.date}
              onChange={(e) => {
                setForm((p) => ({ ...p, date: e.target.value }));
                setFormErrors((prev) => ({ ...prev, date: "" }));
              }}
            />

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Failure Count"
              inputProps={{ min: 0 }}
              value={form.failure_count}
              error={!!formErrors.failure_count}
              helperText={formErrors.failure_count}
              onChange={(e) => {
                const value = e.target.value;
                setForm((p) => ({ ...p, failure_count: value }));
                setFormErrors((prev) => ({ ...prev, failure_count: "" }));
              }}
            />

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Success Count"
              inputProps={{ min: 0 }}
              value={form.success_count}
              error={!!formErrors.success_count}
              helperText={formErrors.success_count}
              onChange={(e) => {
                const value = e.target.value;
                setForm((p) => ({ ...p, success_count: value }));
                setFormErrors((prev) => ({ ...prev, success_count: "" }));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} size="small">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={editMode && !isFormChanged}
            size="small"
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
      </ThemeProvider>
      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={openDeleteDialog}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          cancelDelete();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
          Confirm Delete
        </DialogTitle>

        <DialogContent>
          <Typography fontSize="0.9rem" sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
            Are you sure you want to delete this critical job record?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={cancelDelete} size="small" sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={confirmDelete}
            size="small"
            sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default CriticalJobs;
